import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { bulkOcrUpload, listOcrQueue, confirmOcrItem, discardOcrItem, getVendors } from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";

const NAVY = "#0f172a";
const MAX_FILES = 20;

const ACCEPTED = ".jpg,.jpeg,.png,.pdf";

const STATUS_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  queued:     { label: "Queued",      bg: "#f1f5f9", text: "#475569" },
  processing: { label: "Processing…", bg: "#fef9c3", text: "#854d0e" },
  done:       { label: "Ready",       bg: "#dcfce7", text: "#166534" },
  failed:     { label: "Failed",      bg: "#fee2e2", text: "#991b1b" },
  confirmed:  { label: "Confirmed",   bg: "#eff6ff", text: "#1d4ed8" },
};

const WHT_CATEGORIES = [
  { value: "professional_services", label: "Professional Services (3%)" },
  { value: "advertising",           label: "Advertising (2%)" },
  { value: "rent",                  label: "Rent (5%)" },
  { value: "transport",             label: "Transport (1%)" },
  { value: "other",                 label: "Other" },
];

const BLANK_CONFIRM = {
  vendor_id: "",
  bill_date: "",
  line_items: JSON.stringify([{ description: "", qty: 1, unit_price: 0, amount: 0 }]),
  vat_rate: 7,
  is_vat_claimable: true,
  wht_category: "",
  wht_rate: 0,
  currency: "USD",
  currency_symbol: "$",
  notes: "",
};

function hasActiveItems(items: any[]): boolean {
  return items.some(i => i.status === "queued" || i.status === "processing");
}

export default function BulkReceiptUploadPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<typeof BLANK_CONFIRM>(BLANK_CONFIRM);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmFailures, setConfirmFailures] = useState<Record<number, string>>({});
  const [confirmingSelected, setConfirmingSelected] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["ocr-queue"],
    queryFn: listOcrQueue,
    refetchInterval: (data: any) => hasActiveItems(data ?? []) ? 3000 : false,
  });

  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: () => getVendors() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["ocr-queue"] });

  const discardMut = useMutation({
    mutationFn: discardOcrItem,
    onSuccess: invalidate,
  });

  const confirmMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => confirmOcrItem(id, data),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["vendor-bills"] }); },
  });

  async function handleFiles(files: File[]) {
    const valid = files.filter(f => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return ["jpg", "jpeg", "png", "pdf"].includes(ext || "");
    });
    if (valid.length === 0) { toast("No supported files selected (JPEG, PNG, PDF only)", "error"); return; }
    if (valid.length > MAX_FILES) { toast(`Maximum ${MAX_FILES} files per batch`, "error"); return; }
    setUploading(true);
    try {
      const res: any = await bulkOcrUpload(valid);
      toast(`${res.queued} receipt${res.queued !== 1 ? "s" : ""} queued — AI is extracting data`, "success");
      invalidate();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast(typeof msg === "string" ? msg : JSON.stringify(msg), "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function toggleSelect(id: number, status: string) {
    if (status !== "done") return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function startEdit(item: any) {
    const extracted = item.extracted || {};
    const lineItems = Array.isArray(extracted.line_items) && extracted.line_items.length > 0
      ? JSON.stringify(extracted.line_items)
      : JSON.stringify([{ description: extracted.vendor_name || "Service", qty: 1, unit_price: extracted.total_amount || 0, amount: extracted.total_amount || 0 }]);
    setEditForm({
      vendor_id: item.matched_vendor_id ? String(item.matched_vendor_id) : "",
      bill_date: extracted.bill_date || new Date().toISOString().split("T")[0],
      line_items: lineItems,
      vat_rate: extracted.vat_rate ?? 7,
      is_vat_claimable: true,
      wht_category: extracted.wht_category || "",
      wht_rate: extracted.wht_rate ?? 0,
      currency: extracted.currency || "USD",
      currency_symbol: extracted.currency_symbol || "$",
      notes: "",
    });
    setEditingId(item.id);
  }

  async function handleConfirmSingle(id: number) {
    const form = editingId === id ? editForm : (() => {
      const item = (items as any[]).find((i: any) => i.id === id);
      return buildFormFromItem(item);
    })();
    if (!form.vendor_id) { toast("Select a vendor before confirming", "error"); return; }
    confirmMut.mutate({ id, data: form }, {
      onSuccess: () => { setEditingId(null); setConfirmFailures(p => { const n = { ...p }; delete n[id]; return n; }); },
      onError: (err: any) => {
        const msg = err?.response?.data?.detail || "Confirm failed";
        setConfirmFailures(p => ({ ...p, [id]: typeof msg === "string" ? msg : JSON.stringify(msg) }));
      },
    });
  }

  function buildFormFromItem(item: any) {
    const extracted = item?.extracted || {};
    const lineItems = Array.isArray(extracted.line_items) && extracted.line_items.length > 0
      ? JSON.stringify(extracted.line_items)
      : JSON.stringify([{ description: "Service", qty: 1, unit_price: extracted.total_amount || 0, amount: extracted.total_amount || 0 }]);
    return {
      vendor_id: item?.matched_vendor_id ? String(item.matched_vendor_id) : "",
      bill_date: extracted.bill_date || new Date().toISOString().split("T")[0],
      line_items: lineItems,
      vat_rate: extracted.vat_rate ?? 7,
      is_vat_claimable: true,
      wht_category: extracted.wht_category || "",
      wht_rate: extracted.wht_rate ?? 0,
      currency: extracted.currency || "USD",
      currency_symbol: extracted.currency_symbol || "$",
      notes: "",
    };
  }

  async function handleConfirmSelected() {
    const readyItems = (items as any[]).filter((i: any) => selectedIds.has(i.id) && i.status === "done");
    if (readyItems.length === 0) return;
    setConfirmingSelected(true);
    const failures: Record<number, string> = {};
    for (const item of readyItems) {
      const form = editingId === item.id ? editForm : buildFormFromItem(item);
      if (!form.vendor_id) { failures[item.id] = "No vendor selected"; continue; }
      try {
        await confirmOcrItem(item.id, form);
      } catch (err: any) {
        const msg = err?.response?.data?.detail || "Failed";
        failures[item.id] = typeof msg === "string" ? msg : JSON.stringify(msg);
      }
    }
    setConfirmFailures(prev => ({ ...prev, ...failures }));
    const succeeded = readyItems.length - Object.keys(failures).length;
    if (succeeded > 0) {
      toast(`${succeeded} bill${succeeded !== 1 ? "s" : ""} created as draft`, "success");
      setSelectedIds(new Set());
      invalidate();
      qc.invalidateQueries({ queryKey: ["vendor-bills"] });
    }
    if (Object.keys(failures).length > 0) {
      toast(`${Object.keys(failures).length} item${Object.keys(failures).length !== 1 ? "s" : ""} failed — check highlighted rows`, "error");
    }
    setConfirmingSelected(false);
  }

  const readyCount = (items as any[]).filter((i: any) => selectedIds.has(i.id) && i.status === "done").length;
  const activeCount = (items as any[]).filter((i: any) => i.status === "queued" || i.status === "processing").length;

  return (
    <div>
      <LoadingOverlay active={discardMut.isPending || confirmMut.isPending} />
      <div className="page-header">
        <div>
          <h1>Bulk Receipt Upload</h1>
          <p>
            Upload up to {MAX_FILES} receipts — AI extracts data, you review and confirm
          </p>
        </div>
        <a href="/vendor-bills" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>← Back to Vendor Bills</a>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)); }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? "#1e3a8a" : "#cbd5e1"}`,
          borderRadius: 12, padding: "36px 24px", textAlign: "center",
          cursor: uploading ? "not-allowed" : "pointer", marginBottom: 24,
          background: dragOver ? "#eff6ff" : "#f8fafc",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          style={{ display: "none" }}
          onChange={e => handleFiles(Array.from(e.target.files || []))}
          disabled={uploading}
        />
        {uploading
          ? <p style={{ margin: 0, color: "#64748b" }}>Uploading…</p>
          : <>
              <p style={{ margin: "0 0 6px", fontWeight: 600, color: NAVY }}>
                Drop receipts here or click to select
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>
                JPEG, PNG, or PDF · up to 10 MB each · max {MAX_FILES} files
              </p>
            </>
        }
      </div>

      {activeCount > 0 && (
        <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: "#854d0e" }}>
          {activeCount} receipt{activeCount !== 1 ? "s" : ""} being processed by AI — page refreshes automatically
        </div>
      )}

      {selectedIds.size > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button
            onClick={handleConfirmSelected}
            disabled={readyCount === 0 || confirmingSelected}
            style={{ background: "#1e3a8a", color: "white", border: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: readyCount === 0 || confirmingSelected ? "not-allowed" : "pointer", opacity: readyCount === 0 || confirmingSelected ? 0.5 : 1 }}
          >
            {confirmingSelected ? "Confirming…" : `Confirm Selected (${readyCount})`}
          </button>
        </div>
      )}

      {isLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading queue…</div>}

      {!isLoading && (items as any[]).length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          No receipts in queue. Upload some receipts above to get started.
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {(items as any[]).map((item: any) => {
          const badge = STATUS_BADGE[item.status] || STATUS_BADGE.queued;
          const isReady = item.status === "done";
          const isFailed = item.status === "failed";
          const isEditing = editingId === item.id;
          const failure = confirmFailures[item.id];
          const needsVendor = isReady && !item.matched_vendor_id;

          return (
            <div key={item.id} className="vendor-card-row" style={{
              background: "white",
              border: failure ? "1px solid #fca5a5" : needsVendor ? "1px solid #fde68a" : "1px solid #e2e8f0",
              borderRadius: 12, padding: "14px 18px",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                {isReady && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelect(item.id, item.status)}
                    style={{ marginTop: 4, cursor: "pointer", width: 16, height: 16 }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>
                      {item.vendor_name || "Unknown vendor"}
                    </span>
                    <span style={{ background: badge.bg, color: badge.text, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                      {badge.label}
                    </span>
                    {needsVendor && (
                      <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                        Needs Vendor
                      </span>
                    )}
                    {item.bill_date && <span style={{ fontSize: 12, color: "#64748b" }}>{item.bill_date}</span>}
                    {item.total_amount > 0 && <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{item.total_amount.toFixed(2)}</span>}
                  </div>

                  {isFailed && (
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#dc2626" }}>
                      ⚠ {item.error_message || "OCR extraction failed"}
                    </p>
                  )}
                  {failure && (
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#dc2626" }}>⚠ {failure}</p>
                  )}

                  {/* Inline edit form */}
                  {isEditing && (
                    <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
                      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Vendor *</label>
                          <select value={editForm.vendor_id} onChange={e => setEditForm(f => ({ ...f, vendor_id: e.target.value }))} style={inputStyle}>
                            <option value="">— select vendor —</option>
                            {(vendors as any[]).map((v: any) => (
                              <option key={v.id} value={String(v.id)}>{v.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Bill Date *</label>
                          <DateInput value={editForm.bill_date} onChange={e => setEditForm(f => ({ ...f, bill_date: e.target.value }))} style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>VAT Rate (%)</label>
                          <input type="number" value={editForm.vat_rate} onChange={e => setEditForm(f => ({ ...f, vat_rate: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>WHT Category</label>
                          <select value={editForm.wht_category} onChange={e => setEditForm(f => ({ ...f, wht_category: e.target.value }))} style={inputStyle}>
                            <option value="">None</option>
                            {WHT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Notes</label>
                        <input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={inputStyle} placeholder="Optional notes" />
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => handleConfirmSingle(item.id)}
                          disabled={!editForm.vendor_id || confirmMut.isPending}
                          style={{ ...primaryBtnStyle, opacity: !editForm.vendor_id ? 0.5 : 1 }}
                        >
                          {confirmMut.isPending ? "Creating bill…" : "Create Bill"}
                        </button>
                        <button onClick={() => setEditingId(null)} style={outlineBtnStyle}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Row actions */}
                {!isEditing && (
                  <div className="vendor-card-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {(isReady || isFailed) && (
                      <button onClick={() => startEdit(item)} style={outlineBtnStyle}>
                        {isReady ? "Review & Confirm" : "Edit Manually"}
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (await confirm("Discard this receipt from the queue?", { confirmLabel: "Discard" })) {
                          discardMut.mutate(item.id);
                        }
                      }}
                      style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}
                    >
                      Discard
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "#1e3a8a", color: "white", border: "none",
  padding: "9px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0",
  borderRadius: 6, fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
